/**
 * Online Food Ordering Main Application Component
 */

import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { Box, CircularProgress, CssBaseline, ThemeProvider } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { authApi, menuApi } from '@/services/api';
import { useAppStore } from '@/store/useAppStore';
import { useAuthStore } from '@/store/useAuthStore';
import { useCartStore } from '@/store/useCartStore';
import { getTheme } from '@/theme';
import i18n from './i18n';

// RTL/LTR Emotion caches
const rtlCache = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] });
const ltrCache = createCache({ key: 'muiltr', stylisPlugins: [prefixer] });

import AdminLayout from '@/components/admin/AdminLayout';
import ChatWidget from '@/components/chat/ChatWidget';
import CookieConsentBanner from '@/components/common/CookieConsentBanner';
// Layout components
import MainLayout from '@/components/common/MainLayout';
import ProtectedRoute from '@/components/common/ProtectedRoute';
import ScrollToTop from '@/components/common/ScrollToTop';

// Lazy load pages for code splitting
const Home = lazy(() => import('@/pages/Home'));
const Menu = lazy(() => import('@/pages/Menu'));
const ItemDetail = lazy(() => import('@/pages/ItemDetail'));
const Checkout = lazy(() => import('@/pages/Checkout'));
const OrderConfirmation = lazy(() => import('@/pages/OrderConfirmation'));
const OrderTracking = lazy(() => import('@/pages/OrderTracking'));
const Orders = lazy(() => import('@/pages/Orders'));
const Profile = lazy(() => import('@/pages/Profile'));
const Login = lazy(() => import('@/pages/Login'));
const OAuthCallback = lazy(() => import('@/pages/OAuthCallback'));
const SquareConnectSuccess = lazy(() => import('@/pages/SquareConnectSuccess'));
const NotFound = lazy(() => import('@/pages/NotFound'));

// Legal pages
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));
const CookiePolicy = lazy(() => import('@/pages/CookiePolicy'));
const AllergenInfo = lazy(() => import('@/pages/AllergenInfo'));

// Admin pages
const AdminDashboard = lazy(() => import('@/pages/Admin/Dashboard'));
const AdminOrders = lazy(() => import('@/pages/Admin/Orders'));
const AdminMenu = lazy(() => import('@/pages/Admin/Menu'));
const AdminPromos = lazy(() => import('@/pages/Admin/Promos'));
const AdminSettings = lazy(() => import('@/pages/Admin/Settings'));
const AdminAuditLogs = lazy(() => import('@/pages/Admin/AuditLogs'));

// Loading fallback
const PageLoader = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
    }}
  >
    <CircularProgress color="primary" size={48} />
  </Box>
);

// Cart redirect component - opens drawer and redirects to menu
function CartRedirect() {
  const toggleCartDrawer = useAppStore((state) => state.toggleCartDrawer);

  useEffect(() => {
    toggleCartDrawer();
  }, [toggleCartDrawer]);

  return <Navigate to="/menu" replace />;
}

function App() {
  const themeMode = useAppStore((state) => state.themeMode);
  const language = useAppStore((state) => state.language);
  const restaurant = useAppStore((state) => state.restaurant);
  const setRestaurant = useAppStore((state) => state.setRestaurant);
  const showCookieBanner = useAppStore((state) => state.showCookieBanner);
  const cookieConsent = useAppStore((state) => state.cookieConsent);
  const setCartRestaurantId = useCartStore((state) => state.setRestaurantId);

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const accessToken = useAuthStore((state) => state.accessToken);
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const setLoading = useAuthStore((state) => state.setLoading);

  const direction = language === 'ar' ? 'rtl' : 'ltr';

  // Sync i18next and document direction when language changes
  useEffect(() => {
    i18n.changeLanguage(language);
    document.documentElement.dir = direction;
    document.documentElement.lang = language;
  }, [language, direction]);

  // Use restaurant's primary color for theme, with RTL direction
  const theme = getTheme(themeMode, restaurant?.primary_color, direction);

  // Fetch restaurant data
  const { data: restaurantData } = useQuery({
    queryKey: ['restaurant'],
    queryFn: async () => {
      const response = await menuApi.getRestaurant();
      return response.data;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });

  // Set restaurant in store when data is fetched
  useEffect(() => {
    if (restaurantData) {
      setRestaurant(restaurantData);
      setCartRestaurantId(restaurantData.id);
    }
  }, [restaurantData, setRestaurant, setCartRestaurantId]);

  // Fetch user profile if authenticated (only if we don't have user data yet)
  // This prevents duplicate calls during OAuth callback flow
  useQuery({
    queryKey: ['user', accessToken],
    queryFn: async () => {
      const response = await authApi.getMe();
      setUser(response.data);
      return response.data;
    },
    enabled: isAuthenticated && !!accessToken && !user,
    staleTime: 1000 * 60 * 5,
    retry: false,
    meta: {
      onSettled: () => {
        setLoading(false);
      },
    },
  });

  return (
    <CacheProvider value={direction === 'rtl' ? rtlCache : ltrCache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes with Main Layout */}
            <Route element={<MainLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/menu" element={<Menu />} />
              <Route path="/menu/:categorySlug" element={<Menu />} />
              <Route path="/item/:itemSlug" element={<ItemDetail />} />
              <Route path="/cart" element={<CartRedirect />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/order/:orderNumber" element={<OrderConfirmation />} />
              <Route path="/track/:orderNumber" element={<OrderTracking />} />

              {/* Auth Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/auth/:provider/callback" element={<OAuthCallback />} />
              <Route path="/square/callback" element={<SquareConnectSuccess />} />

              {/* Protected Customer Routes */}
              <Route element={<ProtectedRoute />}>
                <Route path="/orders" element={<Orders />} />
                <Route path="/profile" element={<Profile />} />
              </Route>

              {/* Legal Pages */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/allergens" element={<AllergenInfo />} />

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Route>

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRole="admin">
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="menu" element={<AdminMenu />} />
              <Route path="promos" element={<AdminPromos />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="audit-logs" element={<AdminAuditLogs />} />
            </Route>
          </Routes>
        </Suspense>

        {/* Cookie Consent Banner */}
        {showCookieBanner && !cookieConsent && <CookieConsentBanner />}

        {/* AI Chat Widget (hidden on admin pages) */}
        <ChatWidget />
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;
